import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Palette, Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 PR-B.3 — Theme preference card.
 *
 * Posts `theme_preference` to `PATCH /auth/me/preferences`. Mirrors the value
 * into the global ThemeContext so the change is reflected immediately AND
 * broadcasts to other tabs via BroadcastChannel.
 *
 * Light-only contexts (documented in the card):
 *   - /kiosk/* full-screen sign-in
 *   - PDF reports / badges / print views
 *   - Email templates
 */
const OPTIONS = [
  { value: "auto", label: "Match system", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

export default function ThemeCard() {
  const { preference, setPreference } = useTheme();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const choose = async (next) => {
    if (next === preference) return;
    setBusy(true);
    setPreference(next);  // local + cross-tab apply immediately
    try {
      await api.patch("/auth/me/preferences", { theme_preference: next });
      // Refresh so the user object in context picks up the new pref.
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-navy-100 dark:border-navy-700 bg-white dark:bg-navy-800 p-6"
      data-testid="theme-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <Palette className="w-4 h-4 text-orange-500" /> Theme
          </h2>
          <p className="text-xs text-navy-500 dark:text-navy-300 mt-1">
            Affects the admin shell, dashboards, and settings — applied immediately.
          </p>
        </div>
      </div>

      <fieldset className="grid grid-cols-3 gap-3" disabled={busy}>
        {OPTIONS.map((opt) => {
          const selected = preference === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => choose(opt.value)}
              className={`rounded-lg border px-3 py-3 flex flex-col items-start gap-2 text-left transition-colors ${
                selected
                  ? "border-orange-500 bg-orange-50 dark:bg-orange-500/10 ring-2 ring-orange-200 dark:ring-orange-500/40"
                  : "border-navy-200 dark:border-navy-600 hover:border-navy-300 dark:hover:border-navy-500 bg-white dark:bg-navy-800"
              }`}
              data-testid={`theme-option-${opt.value}`}
            >
              <Icon className={`w-4 h-4 ${selected ? "text-orange-500" : "text-navy-400 dark:text-navy-300"}`} />
              <span className="text-sm font-medium text-navy-900 dark:text-white">{opt.label}</span>
              {selected && <span className="text-[10px] text-orange-600 font-medium uppercase tracking-wider">Selected</span>}
            </button>
          );
        })}
      </fieldset>

      <div className="mt-4 text-xs text-navy-400 dark:text-navy-300 border-t border-navy-100 dark:border-navy-700 pt-3">
        <div className="font-medium mb-1">Known limitations</div>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Kiosk full-screen sign-in (brand-locked light)</li>
          <li>PDF reports, visitor badges, and print views</li>
          <li>Email templates</li>
        </ul>
      </div>
    </div>
  );
}
